import React from 'react'
// libs
import { FieldArray, InjectedFormProps, reduxForm } from 'redux-form'
// hooks
import { useSnackbar } from 'notistack'
// constants
import { Forms } from 'constants/forms'
import { ENTITIES } from 'constants/entities'
import { AVAILABLE_TO } from 'constants/security'
// types
import { IOption } from 'components/FormElements/Select'
// views
import { CopyIcon } from 'components/Modals/PropertyDetailsModal/components/Form/view'
import { Form, ButtonsWrapper } from './views'
// components
import renderUsers from 'components/FormElements/UsersList'
import { Button, BUTTON_LAYOUTS } from 'components/UI'
import { ReduxInput, ReduxSwitch } from 'components/FormElements'
// utils
import { required } from 'utils/validators'

export type DashboardSettingsFormFields = {
  title: string
  active: boolean
  description?: string
  availableTo?: IOption[]
}

interface Props {
  shouldCreate: boolean
  onCancelClick: () => void
}

const AddDashboardForm: React.FC<InjectedFormProps<DashboardSettingsFormFields, Props> & Props> = ({
  shouldCreate,
  handleSubmit,
  onCancelClick
}) => {
  const { enqueueSnackbar } = useSnackbar()

  const prepareOptions = (options: (IOption & { entity: keyof typeof ENTITIES })[]) => {
    return [
      { label: '(All Users)', value: AVAILABLE_TO.ALL },
      { label: '(Managers)', value: AVAILABLE_TO.MANAGERS },
      ...options
        .filter((item) => item.entity === ENTITIES.USER)
        .map((item) => ({
          value: item.value,
          label: item.label
        }))
    ]
  }

  const onCopyClick = () => {
    navigator.clipboard.writeText('test')

    enqueueSnackbar('Copied!', {
      variant: 'info',
      autoHideDuration: 1000
    })
  }

  return (
    <Form onSubmit={handleSubmit}>
      {!shouldCreate && (
        <ReduxInput
          label='Dashboard ID'
          name='dashboardId'
          disabled
          endAdornment={<CopyIcon onClick={onCopyClick} />}
        />
      )}
      <ReduxInput label='Title' name='title' validate={required} />
      <ReduxInput multiline label='Description' name='description' validate={required} />
      <ReduxSwitch label='Active' name='active' />
      <FieldArray name='availableTo' component={renderUsers} prepareOptions={prepareOptions} />
      <ButtonsWrapper>
        <Button type='submit' layout={BUTTON_LAYOUTS.PURPLE}>
          {shouldCreate ? 'Create' : 'Save'}
        </Button>
        <Button type='button' layout={BUTTON_LAYOUTS.PURPLE_INVERTED} onClick={onCancelClick}>
          Cancel
        </Button>
      </ButtonsWrapper>
    </Form>
  )
}

export default reduxForm<DashboardSettingsFormFields, Props>({
  form: Forms.DASHBOARD_SETTINGS_FORM,
  destroyOnUnmount: false,
  enableReinitialize: true
})(AddDashboardForm)
